// ============================================================================
// The upload queue (docs/areas/files.md): what the device is sending, with its
// progress, for the tray in the Files app. Each upload writes its File record
// to Dexie first (never lose input), then sends the bytes to the bucket
// (lib/files/upload.ts), then applies `complete` as bookkeeping (not on undo).
// A multipart upload's id is kept in localStorage so a reload can resume it:
// pick the same file again and only the missing parts go.
// ============================================================================

import { create } from 'zustand'
import { v7 as uuidv7 } from 'uuid'
import { db, mutate } from '@/runtime'
import type { FileResource } from '@/kinds/file/apply'
import { useToasts } from '@/state/toasts'
import { filesApi } from './api'
import { makeThumbnail } from './thumbs'
import { guessType, thumbable } from './types'
import { DEDUPE_MAX, hashFile, sendBytes, UploadCancelled } from './upload'
import { freeName, childrenOf } from './tree'

export type UploadState = 'hashing' | 'sending' | 'done' | 'failed' | 'cancelled'

export interface Upload {
  id: string
  name: string
  size: number
  sent: number
  state: UploadState
  error?: string
  /** the bytes were already in the bucket: nothing was sent */
  reused?: boolean
}

interface Resumable {
  key: string
  uploadId: string
  name: string
  size: number
}

const RESUME = 'files-resume'
const readResume = (): Record<string, Resumable> => {
  try {
    return JSON.parse(localStorage.getItem(RESUME) ?? '{}') as Record<string, Resumable>
  } catch {
    return {}
  }
}
const writeResume = (id: string, r: Resumable | null) => {
  try {
    const all = readResume()
    if (r) all[id] = r
    else delete all[id]
    localStorage.setItem(RESUME, JSON.stringify(all))
  } catch {
    /* private mode: no resume after a reload */
  }
}

/** Whether this device can resume a File's upload (it started it as a multipart upload). */
export const resumableHere = (id: string): Resumable | undefined => readResume()[id]

const controllers = new Map<string, AbortController>()

interface UploadsState {
  uploads: Upload[]
  set(id: string, patch: Partial<Upload>): void
  add(u: Upload): void
  clearDone(): void
}

export const useUploads = create<UploadsState>((set, get) => ({
  uploads: [],
  set(id, patch) {
    set({ uploads: get().uploads.map((u) => (u.id === id ? { ...u, ...patch } : u)) })
  },
  add(u) {
    set({ uploads: [...get().uploads, u] })
  },
  clearDone() {
    set({ uploads: get().uploads.filter((u) => u.state === 'hashing' || u.state === 'sending') })
  },
}))

const key = () => `o/${crypto.randomUUID()}`

/** A ready file elsewhere whose bytes are these (same sha256, not purged): its object can be shared. */
const sameBytes = async (sha: string): Promise<FileResource | undefined> => {
  const all = (await db.resources.where('kind').equals('File').toArray()) as FileResource[]
  return all.find(
    (f) => f.spec.sha256 === sha && f.status.state === 'ready' && !f.status.purged_at && f.spec.key,
  )
}

/** Upload files into a folder of a suite. Resolves when all have finished one way or another. */
export const uploadFiles = async (files: File[], parent: string | null, ws?: string): Promise<void> => {
  const siblings = (await db.resources.where('kind').equals('File').toArray()) as FileResource[]
  const taken = childrenOf(siblings, parent)
  await Promise.all(
    files.map(async (file) => {
      const name = freeName(taken, file.name || 'file')
      taken.push({ spec: { name } } as FileResource)
      await uploadOne(file, name, parent, ws)
    }),
  )
}

const uploadOne = async (file: File, name: string, parent: string | null, ws?: string) => {
  const id = uuidv7()
  const ctrl = new AbortController()
  controllers.set(id, ctrl)
  const ui = useUploads.getState()
  ui.add({ id, name, size: file.size, sent: 0, state: file.size <= DEDUPE_MAX ? 'hashing' : 'sending' })
  const contentType = guessType(name, file.type)
  const labels: Record<string, string> = ws ? { ws } : {}
  try {
    let sha: string | undefined
    if (file.size <= DEDUPE_MAX) {
      sha = await hashFile(file, (read) => ui.set(id, { sent: read }), ctrl.signal)
      const twin = await sameBytes(sha)
      if (twin) {
        await mutate.grouped(async () => {
          await mutate.create(
            'File',
            {
              name,
              ...(parent ? { parent } : {}),
              size: file.size,
              content_type: contentType,
              key: twin.spec.key!,
              ...(twin.spec.thumb_key ? { thumb_key: twin.spec.thumb_key } : {}),
              sha256: sha,
            },
            labels,
            id,
          )
          await mutate.bookkeeping(() => mutate.action(id, 'complete'))
        })
        ui.set(id, { state: 'done', sent: file.size, reused: true })
        return
      }
    }
    const objectKey = key()
    const thumb = thumbable(name, contentType) ? await makeThumbnail(file) : null
    const thumbKey = thumb ? key() : undefined
    await mutate.create(
      'File',
      {
        name,
        ...(parent ? { parent } : {}),
        size: file.size,
        content_type: contentType,
        key: objectKey,
        ...(thumbKey ? { thumb_key: thumbKey } : {}),
        ...(sha ? { sha256: sha } : {}),
      },
      labels,
      id,
    )
    ui.set(id, { state: 'sending', sent: 0 })
    if (thumb && thumbKey) {
      // a thumbnail that does not get there only costs the grid its picture
      await sendBytes({ key: thumbKey, file: thumb, contentType: 'image/jpeg', onProgress: () => {} }).catch(
        () => {},
      )
    }
    await sendBytes({
      key: objectKey,
      file,
      contentType,
      signal: ctrl.signal,
      onProgress: (sent) => ui.set(id, { sent }),
      onUploadId: (uploadId) => writeResume(id, { key: objectKey, uploadId, name, size: file.size }),
    })
    writeResume(id, null)
    await mutate.bookkeeping(() => mutate.action(id, 'complete'))
    ui.set(id, { state: 'done', sent: file.size })
    if (!sha) {
      // too big to hash first: hash now, so a later copy of it is not sent again
      const later = await hashFile(file).catch(() => undefined)
      if (later) await mutate.bookkeeping(() => mutate.patch(id, { spec: { sha256: later } })).catch(() => {})
    }
  } catch (e) {
    if (e instanceof UploadCancelled) {
      ui.set(id, { state: 'cancelled' })
    } else {
      ui.set(id, { state: 'failed', error: (e as Error).message })
      useToasts.getState().show(`“${name}” did not upload: ${(e as Error).message}`)
    }
  } finally {
    controllers.delete(id)
  }
}

/** Stop an upload: its multipart parts are dropped in the bucket and its File goes. */
export const cancelUpload = async (id: string): Promise<void> => {
  controllers.get(id)?.abort()
  const r = (await db.resources.get(id)) as FileResource | undefined
  const resume = resumableHere(id)
  if (r?.spec.key) await filesApi.uploadAbort(r.spec.key, resume?.uploadId).catch(() => {})
  writeResume(id, null)
  if (r && !r.deleted_at && r.status.state === 'uploading') await mutate.bookkeeping(() => mutate.remove(id))
}

/** Carry on an upload this device started before a reload: the same file, picked again. */
export const resumeUpload = async (r: FileResource, file: File): Promise<void> => {
  const info = resumableHere(r.id)
  if (!info || !r.spec.key) throw new Error('this upload cannot be resumed here; upload the file again')
  if (file.size !== info.size) throw new Error('that is not the same file (its size differs)')
  const ctrl = new AbortController()
  controllers.set(r.id, ctrl)
  const ui = useUploads.getState()
  if (!ui.uploads.some((u) => u.id === r.id))
    ui.add({ id: r.id, name: r.spec.name, size: file.size, sent: 0, state: 'sending' })
  else ui.set(r.id, { state: 'sending', error: undefined })
  try {
    await sendBytes({
      key: r.spec.key,
      file,
      contentType: r.spec.content_type ?? guessType(r.spec.name),
      signal: ctrl.signal,
      uploadId: info.uploadId,
      onProgress: (sent) => ui.set(r.id, { sent }),
    })
    writeResume(r.id, null)
    await mutate.bookkeeping(() => mutate.action(r.id, 'complete'))
    ui.set(r.id, { state: 'done', sent: file.size })
  } catch (e) {
    ui.set(
      r.id,
      e instanceof UploadCancelled
        ? { state: 'cancelled' }
        : { state: 'failed', error: (e as Error).message },
    )
    throw e
  } finally {
    controllers.delete(r.id)
  }
}
